import React, { useRef, KeyboardEvent, useEffect } from 'react';
import styled from 'styled-components';

import { Flex, Input } from 'components';

const InputsWrapper = styled(Flex)`
  flex-wrap: nowrap;
  gap: 5px;
`;

interface ConfirmationCodeProps {
  setConfirmationCode: React.Dispatch<React.SetStateAction<string | null>>;
  confirmationCode: string | null;
  hasError?: boolean;
}

const ConfirmationCodeInput = ({ confirmationCode, setConfirmationCode, hasError }: ConfirmationCodeProps) => {
  const form = useRef<HTMLFormElement>(null);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const handleInputChange = (index: number, e: React.FormEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    if (!(input instanceof HTMLInputElement)) return; // Skip if target is not an input element
    const value = input.value;

    // Update the verification code state
    const newCodeArray = confirmationCode?.split('') || [];
    newCodeArray[index] = value;
    setConfirmationCode(newCodeArray.join(''));

    // Move to the next input if a character is entered
    if (value && index < inputs.current.length - 1) {
      inputs.current[index + 1]?.focus();
    }
    // Move to the previous input if the character is deleted
    else if (!value && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData?.getData('text') || '';
    const codeArray = pasteData.slice(0, 6).split('');

    let newConfirmationCode = '';

    // Populate the input fields with pasted data
    codeArray.forEach((char, index) => {
      if (inputs.current[index]) {
        inputs.current[index]!.value = char;
        handleInputChange(index, e);
        newConfirmationCode += char;
      }
    });

    setConfirmationCode(newConfirmationCode);
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowRight' && index < inputs.current.length - 1) {
      e.preventDefault();
      inputs.current[index + 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputs.current[index - 1]?.focus();
    }
  };

  const handleClearInputs = () => {
    form.current?.reset();
    inputs.current[0]?.focus();
  };

  useEffect(() => {
    if (confirmationCode === null) handleClearInputs();
  }, [confirmationCode]);

  return (
    <form ref={form}>
      <InputsWrapper>
        {[...Array(6)].map((_, index) => (
          <Input
            key={index}
            ref={(el) => (inputs.current[index] = el)}
            type="text"
            maxLength={1}
            onInput={(e) => handleInputChange(index, e)}
            onPaste={handlePaste}
            onKeyDown={(e) => handleKeyDown(index, e)}
            style={{ textAlign: 'center', fontSize: 16, padding: 0, height: 36 }}
            autoFocus={index === 0}
            data-error={hasError}
          />
        ))}
      </InputsWrapper>
    </form>
  );
};

export default ConfirmationCodeInput;
